import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request, getInfoLocalStorage } from "../api/config"

const stateFromLocalStorage = getInfoLocalStorage() 

const initialState = {
    isLoading: false,
    success: false,
    message: null,
    saques: [],
    saque: null,
    isSubmit: false,
    successAction: false,
    pagination: {
        total: 0,
        current: 1,
        pageSize: 10
    }
}

// Get saques with specific status
export const getSaques = createAsyncThunk(
    'saques/getSaquesPendentes',
    async ({ status, tipo }, thunkAPI) => {
        try {
            // O status pode ser o código numérico (0,1,2,3) ou string ('pending','approved','rejected','finalized')
            const statusParam = typeof status === 'string' ? status : status.toString();
            
            // Construir a URL no formato exato esperado pela API
            const tipoParam = tipo ? `&tipo=${tipo}` : '&tipo=contract';
            const url = `resgates/getResgatesPendentes?status=${statusParam}${tipoParam}`;
            
            const res = await request(url)
            
            // Log response for debugging
            console.log("API Response getSaques:", res);
            
            if(res.code === 51) {
                // Ensure each resgate has the required properties
                const saques = res.resgates.map(resgate => {
                    // Ensure podeAprovar is properly set
                    if (resgate.podeAprovar === undefined) {
                        resgate.podeAprovar = true; // Default to true if not present
                    }
                    
                    // Ensure saldoDisponivel is properly set
                    if (resgate.saldoDisponivel === undefined) {
                        resgate.saldoDisponivel = resgate.valor; // Default to valor if not present
                    }
                    
                    return resgate;
                });
                
                return { saques }
            } else {
                return thunkAPI.rejectWithValue(res.message)
            }
        } catch (e) {
            return thunkAPI.rejectWithValue('Houve um erro ao retornar as solicitações de saque')
        }
    }
)

// Get saque details
export const getSaqueDetails = createAsyncThunk(
    'saques/getSaqueDetails',
    async (id, thunkAPI) => {
        try {
            const url = `resgates/getResgate/${id}`;
            const res = await request(url)
            
            // Log response for debugging
            console.log("API Response getSaqueDetails:", res);
            
            if(res.code === 51) {
                // Make sure the detailed response has the same structure as the list response
                const saque = res.resgate;
                
                // Ensure podeAprovar and saldoDisponivel are properly set
                // If they don't exist in the response, set default values
                if (saque.podeAprovar === undefined) {
                    saque.podeAprovar = true; // Default to true if not present
                }
                
                if (saque.saldoDisponivel === undefined) {
                    saque.saldoDisponivel = saque.valor; // Default to valor if not present
                }
                
                return { saque }
            } else {
                return thunkAPI.rejectWithValue(res.message)
            }
        } catch (e) {
            return thunkAPI.rejectWithValue('Houve um erro ao retornar os detalhes do saque')
        }
    }
)

// Approve saque
export const approveSaque = createAsyncThunk(
    'saques/confirmResgate',
    async (id, thunkAPI) => {
        try {
            const adminId = stateFromLocalStorage.id
            const res = await request('resgates/confirmResgate', 'PUT', {
                resgate: id,
                idContrato: id, // Usar mesmo id do resgate para idContrato
                adminId,
                tipo: 'balance'
            })
            if(res.code === 51) {
                return res
            } else {
                return thunkAPI.rejectWithValue(res.message)
            }
        } catch (e) {
            return thunkAPI.rejectWithValue('Houve um erro ao aprovar o saque')
        }
    }
)

// Reject saque
export const rejectSaque = createAsyncThunk(
    'saques/denyResgate',
    async ({ id, motivo }, thunkAPI) => {
        try {
            const adminId = stateFromLocalStorage.id
            const res = await request('resgates/denyResgate', 'PUT', {
                resgate: id,
                idContrato: id, // Usar mesmo id do resgate para idContrato
                adminId,
                motivo,
                tipo: 'balance'
            })
            if(res.code === 51) {
                return res
            } else {
                return thunkAPI.rejectWithValue(res.message)
            }
        } catch (e) {
            return thunkAPI.rejectWithValue('Houve um erro ao rejeitar o saque')
        }
    }
)

// Finalize saque
export const finalizeSaque = createAsyncThunk(
    'saques/finalizarResgate',
    async ({ id, comprovante }, thunkAPI) => {
        try {
            const adminId = stateFromLocalStorage.id
            
            // Create FormData object for file upload
            const formData = new FormData()
            formData.append('resgate', id)
            formData.append('idContrato', id)
            formData.append('adminId', adminId)
            formData.append('file', comprovante)
            formData.append('tipo', 'balance')
            
            const res = await request('resgates/finalizarResgate', 'PUT', formData, true)
            if(res.code === 51) {
                return res
            } else {
                return thunkAPI.rejectWithValue(res.message)
            }
        } catch (e) {
            return thunkAPI.rejectWithValue('Houve um erro ao finalizar o saque')
        }
    }
)

const saquesSlice = createSlice({
    name: 'saques',
    initialState,
    reducers: {
        resetSuccessAction: (state) => {
            state.successAction = false
        },
        resetErrorMessage: (state) => {
            state.message = null
        }
    },
    extraReducers(builder) {
        builder
            // Get saques
            .addCase(getSaques.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getSaques.fulfilled, (state, { payload }) => {
                state.isLoading = false
                state.success = true
                state.saques = payload.saques
            })
            .addCase(getSaques.rejected, (state, { payload }) => {
                state.isLoading = false
                state.success = false
                state.message = payload
            })
            
            // Get saque details
            .addCase(getSaqueDetails.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getSaqueDetails.fulfilled, (state, { payload }) => {
                state.isLoading = false
                state.success = true
                state.saque = payload.saque
            })
            .addCase(getSaqueDetails.rejected, (state, { payload }) => {
                state.isLoading = false
                state.success = false
                state.message = payload
            })
            
            // Approve saque
            .addCase(approveSaque.pending, (state) => {
                state.isSubmit = true
            })
            .addCase(approveSaque.fulfilled, (state) => {
                state.isSubmit = false
                state.successAction = true
            })
            .addCase(approveSaque.rejected, (state, { payload }) => {
                state.isSubmit = false
                state.successAction = false
                state.message = payload
            })
            
            // Reject saque
            .addCase(rejectSaque.pending, (state) => {
                state.isSubmit = true
            })
            .addCase(rejectSaque.fulfilled, (state) => {
                state.isSubmit = false
                state.successAction = true
            })
            .addCase(rejectSaque.rejected, (state, { payload }) => {
                state.isSubmit = false
                state.successAction = false
                state.message = payload
            })
            
            // Finalize saque
            .addCase(finalizeSaque.pending, (state) => {
                state.isSubmit = true
            })
            .addCase(finalizeSaque.fulfilled, (state) => {
                state.isSubmit = false
                state.successAction = true
            })
            .addCase(finalizeSaque.rejected, (state, { payload }) => {
                state.isSubmit = false
                state.successAction = false
                state.message = payload
            })
    }
})

export const { resetSuccessAction, resetErrorMessage } = saquesSlice.actions
export default saquesSlice.reducer 